/* eslint-disable react/jsx-props-no-spreading */
import React, {ReactNode} from 'react';
import {HeaderGroup} from 'react-table';
import styled, {css} from 'styled-components';

import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {RandomSkeleton} from '../../../components/common/randomSkeleton';
import {CustomTableHeaderGroup} from '../reportElements/table/tableHelpers';
import {stickyColumn} from './tableCommons';

/*
 * Props.
 */

interface TableHeaderRowProps<T extends object> {
  headerGroup: HeaderGroup<T>;
  isFirstColumnSticky?: boolean;
  hasOverflow?: boolean;
  hasScrolled?: boolean;
  isLoading?: boolean;
}

/*
 * Styles.
 */

const StyledHeader = styled.span<{$isSorted: boolean}>`
  display: inline-block;
  vertical-align: middle;
  font-weight: ${fontWeights.semibold};
  color: ${(p) => (p.$isSorted ? p.theme.greys.shade90 : p.theme.greys.shade70)};
`;

const StyledHeaderRow = styled.tr`
  background-color: ${(p) => p.theme.foreground};
`;

const StyledHeaderCell = styled.th<{$isSticky: boolean}>`
  cursor: default !important;
  font-weight: ${fontWeights.normal};
  padding: 11px 12px;
  border-bottom: 2px solid ${(p) => p.theme.greys.shade40};
  white-space: nowrap;
  &:hover ${StyledHeader} {
    color: ${(p) => p.theme.greys.shade90};
  }

  ${(p) => p.$isSticky && stickyColumn}
`;

const StyledSortIcon = styled(Icon)<{$isSorted: boolean}>`
  display: inline-block;
  vertical-align: middle;

  margin-left: 2px;

  ${(p) =>
    !p.$isSorted &&
    css`
      visibility: hidden;
    `};
`;

/*
 * Component.
 */

export const TableHeaderRow = <T extends Object>({
  isFirstColumnSticky,
  hasOverflow,
  headerGroup,
  hasScrolled,
  isLoading,
}: TableHeaderRowProps<T>) => {
  const {headers} = headerGroup;

  return (
    <StyledHeaderRow {...headerGroup.getHeaderGroupProps()}>
      {
        // Loop over the headers in each row
        headers.map((header) => (
          // Apply the header cell props
          <StyledHeaderCell
            {...header.getHeaderProps(header.getSortByToggleProps())}
            title={undefined}
            $isSticky={Boolean(isFirstColumnSticky && hasOverflow && header.index === 0 && hasScrolled)}
          >
            {renderHeader(header, isLoading)}
          </StyledHeaderCell>
        ))
      }
    </StyledHeaderRow>
  );
};

/*
 * Helpers.
 */

function renderHeader<T extends object>(header: HeaderGroup<T>, isLoading?: boolean) {
  const hasHeader = typeof header.render('Header') === 'object';

  if (isLoading && !hasHeader) {
    return <RandomSkeleton />;
  }

  return maybeWrapInTooltip(
    header,
    <>
      <StyledHeader $isSorted={header.isSorted}>{header.render('Header')}</StyledHeader>
      {header.canSort && (
        <UserThemeConsumer>
          {(theme) => (
            <StyledSortIcon
              color={theme.greys.shade90}
              $isSorted={header.isSorted}
              name={header.isSortedDesc ? 'arrowDownThickSmall' : 'arrowUpThickSmall'}
            />
          )}
        </UserThemeConsumer>
      )}
    </>,
  );
}

function maybeWrapInTooltip<T extends object>(header: HeaderGroup<T>, children: ReactNode) {
  const {tooltip} = header as CustomTableHeaderGroup<T>;
  if (tooltip && typeof tooltip === 'function') {
    return tooltip(children);
  }

  return children;
}
